/**
 * DCP source
 * @module
 */
var _ = require('optimizely-client-fn');

var AsyncRequest = require('core/utils/async_request');
var createError = require('core/utils/custom_error').create;
var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var PromiseEnv = require('core/lib/promise').Promise;
var XHR = require('core/utils/xhr');

var DCPError = exports.DCPError = createError('DCPError');

var SOURCE_ID = 'dcps';  // Dynamic Customer Profile Service.

/**
 * Returns a promise that resolves to an object containing DCP data.
 * @param {DCPSnippetRequestDescriptor|DCPProjectRequestDescriptor} options See typedef in src/core/types.js.
 * @return {Promise}
 */
exports.fetchDCPData = function(options:DCPSnippetRequestDescriptor|DCPProjectRequestDescriptor): Promise<any> {
  var url = getBaseURL(options);
  if (!url) {
    return PromiseEnv.resolve({});
  }

  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('sendDCP');
  var makeRequest = _.partial(XHR.request, {
    url: url,
  });

  var source = SOURCE_ID + '_' + url;
  return AsyncRequest.makeAsyncRequest(source, function() {
    makeRequest().then(function(res) {
      var data;
      if (res.status > 399) {
        // Intentionally swallow response body since it might expose implementation details (AUD-1605)
        throw new DCPError(res.status + ': ' + res.statusText);
      }

      try {
        data = JSONEnv.parse(res.response);
      } catch(e) {
        throw new DCPError('Error parsing response: ' + e.toString());
      }

      if (!data) {
        throw new DCPError('No data received');
      }

      return data;
    }, function(xhr) {
      throw new DCPError(xhr.status + ': ' + xhr.statusText);
    }).then(
      _.partial(AsyncRequest.resolveRequest, source),
      _.partial(AsyncRequest.rejectRequest, source)
    );
  }).then(function(data) {
    if (__INVARIANT__) {
      require('core/utils/invariant').isType('DCPData', data);
    }

    return data;
  });
};

// See https://github.com/optimizely/vissandara-apis#get-targetingprojectidcustomerid
var DCP_TARGETING_URL = __DCP_TARGETING_URL__;

/**
 * @param {DCPSnippetRequestDescriptor|DCPProjectRequestDescriptor} options See typedef in src/types.js.
 * @return {string|null}
 * @private
 */
function getBaseURL(options:DCPSnippetRequestDescriptor|DCPProjectRequestDescriptor): string|null {
  var resourcePath;
  if ((<any>options).projectId) {
    let dcpOptions = options as DCPProjectRequestDescriptor;

    if (__INVARIANT__) {
      require('core/utils/invariant').isType('DCPProjectRequestDescriptor', options);
    }
    // https://github.com/optimizely/vissandara-apis/blob/devel/API.md#get-targetingprojectidoptlydatasourceidcustomerid
    // /targeting/:projectId/:optlyDatasourceId/:customerId
    if (!dcpOptions.optlyDatasourceId) {
      // This should never happen.
      Logger.error('DCP / No Optimizely datasource id passed to getBaseURL.');
      return null;
    }

    resourcePath = [
      dcpOptions.projectId,
      dcpOptions.optlyDatasourceId.toString(),
      dcpOptions.uid
    ];
  } else {
    let dcpOptions = options as DCPSnippetRequestDescriptor;

    if (__INVARIANT__) {
      require('core/utils/invariant').isType('DCPSnippetRequestDescriptor', options);
    }
    // Multi-project snippet
    // https://github.com/optimizely/vissandara-apis/blob/devel/API.md#get-targetingsnippetsnippetiddcpserviceidcustomerid
    // /targeting/snippet/:snippetId/:dcpServiceId/:customerId
    resourcePath = [
      'snippet',
      dcpOptions.snippetId,
      dcpOptions.dcpServiceId,
      dcpOptions.uid,
    ];
  }

  var urlPath = _.map(resourcePath, encodeURIComponent);
  var url = [DCP_TARGETING_URL].concat(urlPath).join('/');

  // Piggy-back aliasing onto this targeting request, *if* aliasing needs to happen.
  // Despite still being a GET request, this mutates DCP's persisted aliases:
  // https://github.com/optimizely/vissandara-apis/blob/4ec25c1f84b54fdd0c40dd7ea60f9a314cef958a/routes/targeting.js#L178-L183
  //
  // In theory aliases could only be sent when current aliases differ from those cached,
  // or if the cache was empty, since both cases signal a possible 'change of visitor identity'.
  // However, due to caching in TAPI, a strategy where we initially send aliases and then don't
  // when the cache is fresh would lead to behavior where client re-activations used cached data
  // but asynchronously revalidate against the TAPI response. If the reactivation happened before
  // the TAPI TTL expired, the client's cache will be poisoned with a stale value. See CJS-2415
  // for an example of this.
  var aliasParams = _.map(options.currentAliases, function(v, k) {
    return encodeURIComponent(k) + '=' + encodeURIComponent(v);
  });

  if (!_.isEmpty(aliasParams)) {
    url += '?' + aliasParams.join('&');
  }

  return url;
}

if (__TEST__) {
  exports.SOURCE_ID = SOURCE_ID;
  exports.DCP_TARGETING_URL = DCP_TARGETING_URL;
}
